export async function resizeFileToBase64(file: File, size = 512, quality = 0.8): Promise<string> {
  // Create an ImageBitmap when supported for better performance
  let imgBitmap: ImageBitmap | null = null;
  try {
    imgBitmap = await createImageBitmap(file);
  } catch {
    // Fallback to using HTMLImageElement
  }

  // If createImageBitmap failed, use Image element
  if (!imgBitmap) {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(new Error('Failed to read file'));
      fr.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image'));
      el.src = dataUrl;
    });
    imgBitmap = await createImageBitmap(img);
  }

  const iw = imgBitmap.width;
  const ih = imgBitmap.height;
  // Crop to square center
  let sx = 0, sy = 0, sWidth = iw, sHeight = ih;
  if (iw > ih) {
    sx = Math.floor((iw - ih) / 2);
    sWidth = ih;
    sHeight = ih;
  } else if (ih > iw) {
    sy = Math.floor((ih - iw) / 2);
    sWidth = iw;
    sHeight = iw;
  }

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  ctx.drawImage(imgBitmap, sx, sy, sWidth, sHeight, 0, 0, size, size);

  // Convert to blob (jpeg) and then to dataURL
  const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('Failed to create image blob');

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('Failed to read blob'));
    fr.readAsDataURL(blob);
  });

  // Strip data URL prefix
  const comma = dataUrl.indexOf(',');
  return dataUrl.slice(comma + 1);
}

/**
 * Resize image to fit under a maximum file size by adjusting quality and dimensions
 * @param file - Image file to resize
 * @param maxSizeBytes - Maximum size in bytes (default 2MB)
 * @param initialMaxDimension - Starting max dimension (default 800px)
 * @returns Base64 encoded image string
 */
export async function resizeToMaxSize(
  file: File,
  maxSizeBytes = 2 * 1024 * 1024,
  initialMaxDimension = 800
): Promise<string> {
  // Load image
  let imgBitmap: ImageBitmap | null = null;
  try {
    imgBitmap = await createImageBitmap(file);
  } catch {
    // Fallback to using HTMLImageElement
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(new Error('Failed to read file'));
      fr.readAsDataURL(file);
    });
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Failed to load image'));
      el.src = dataUrl;
    });
    imgBitmap = await createImageBitmap(img);
  }

  const originalWidth = imgBitmap.width;
  const originalHeight = imgBitmap.height;

  // Try different combinations of size and quality
  const attempts = [
    { maxDimension: initialMaxDimension, quality: 0.85 },
    { maxDimension: initialMaxDimension, quality: 0.75 },
    { maxDimension: initialMaxDimension, quality: 0.65 },
    { maxDimension: 600, quality: 0.85 },
    { maxDimension: 600, quality: 0.75 },
    { maxDimension: 600, quality: 0.65 },
    { maxDimension: 400, quality: 0.85 },
    { maxDimension: 400, quality: 0.75 },
    { maxDimension: 400, quality: 0.65 },
    { maxDimension: 300, quality: 0.75 },
    { maxDimension: 300, quality: 0.65 },
  ];

  for (const { maxDimension, quality } of attempts) {
    // Calculate new dimensions maintaining aspect ratio
    let width = originalWidth;
    let height = originalHeight;
    
    if (width > maxDimension || height > maxDimension) {
      if (width > height) {
        height = Math.round((height * maxDimension) / width);
        width = maxDimension;
      } else {
        width = Math.round((width * maxDimension) / height);
        height = maxDimension;
      }
    }

    // Create canvas and draw resized image
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    ctx.drawImage(imgBitmap, 0, 0, width, height);

    // Convert to blob
    const blob: Blob | null = await new Promise((resolve) => 
      canvas.toBlob(resolve, 'image/jpeg', quality)
    );
    
    if (!blob) continue;

    // Check if size is acceptable
    if (blob.size <= maxSizeBytes) {
      // Convert to base64
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(new Error('Failed to read blob'));
        fr.readAsDataURL(blob);
      });

      // Strip data URL prefix
      const comma = dataUrl.indexOf(',');
      return dataUrl.slice(comma + 1);
    }
  }

  // If all attempts failed, return the smallest version we tried
  const canvas = document.createElement('canvas');
  const minDimension = 300;
  let width = originalWidth;
  let height = originalHeight;
  
  if (width > minDimension || height > minDimension) {
    if (width > height) {
      height = Math.round((height * minDimension) / width);
      width = minDimension;
    } else {
      width = Math.round((width * minDimension) / height);
      height = minDimension;
    }
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  ctx.drawImage(imgBitmap, 0, 0, width, height);

  const blob: Blob | null = await new Promise((resolve) => 
    canvas.toBlob(resolve, 'image/jpeg', 0.6)
  );
  
  if (!blob) throw new Error('Failed to create image blob');

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error('Failed to read blob'));
    fr.readAsDataURL(blob);
  });

  const comma = dataUrl.indexOf(',');
  return dataUrl.slice(comma + 1);
}

export default resizeFileToBase64;
